/**
 * The profile's zone against the device's, and the answer the user gives.
 *
 * A profile left in the wrong zone is the silent version of the midnight bug
 * and the worse one: it files evening meals under the following day, correctly,
 * forever, according to a zone the user does not live in. Both clients can see
 * both zones, so both clients say so — and both have to treat "I'm travelling,
 * keep it" as a real answer rather than a postponement, or the warning becomes
 * a banner that is simply always there. A warning that is always there is
 * furniture, and furniture is ignored on the one day it means something.
 *
 * The answer is kept **per device**, because devices are exactly what disagree:
 * the same account is a phone that follows the traveller and a desktop left on
 * UTC, and an answer given on one says nothing about the other. That is why
 * none of this lives on the profile.
 *
 * What lives *here* is the part the two clients must agree on — how a pairing
 * is named, when a stored answer covers the situation on screen, and how many
 * to keep. The storage itself does not: the web has synchronous `localStorage`
 * and the phone has asynchronous `AsyncStorage`, so each wraps these in its own
 * way, the same split as the offline queue. Keeping the rules in one place is
 * what stops the two clients from quietly disagreeing about when to ask.
 */

/**
 * How far apart two zones are right now, in whole hours.
 *
 * Computed rather than looked up, because the answer moves with daylight saving
 * and a fixed table would be wrong twice a year. The number is what makes the
 * warning concrete: "six hours apart" explains the consequence in a way that
 * naming two zones does not.
 *
 * Null when either zone is not one the platform knows — a warning that cannot
 * state the gap is still worth showing, so this failing must not take the
 * banner down with it.
 */
export function hoursApart(a: string, b: string, at: Date = new Date()): number | null {
  try {
    const read = (zone: string) =>
      Number(
        new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", hour12: false }).format(
          at,
        ),
      );
    const diff = read(a) - read(b);
    // Wrap: 23 and 1 are two hours apart, not twenty-two.
    const wrapped = ((diff + 36) % 24) - 12;
    return Math.abs(wrapped);
  } catch {
    return null;
  }
}

/**
 * How many answered pairings a device remembers.
 *
 * A frequent traveller answers once per place rather than once per trip, and
 * eight is well past the number of zones a person is regularly in while keeping
 * the stored value small enough that pruning never matters.
 */
export const KEPT_ZONES_LIMIT = 8;

/**
 * One answered pairing, named.
 *
 * The *pair* and not the device alone, because "keep it" is an answer about a
 * situation: keep Europe/Zurich while this device says Europe/Rome. When either
 * half moves the situation is new — the traveller flies on to a different
 * number of misfiled hours, or the profile relocates and the old answer no
 * longer describes anything — and a new situation is worth one question.
 *
 * Order within the pair is fixed, profile then device, so the same two zones
 * the other way round is a different key. It is a different case: which of the
 * two the diary is kept in is the whole question.
 */
export function zonePair(profileZone: string, deviceZone: string): string {
  return `${profileZone}|${deviceZone}`;
}

/** Has this device already been told to keep the profile zone in this place? */
export function zoneKept(
  kept: readonly string[],
  profileZone: string,
  deviceZone: string,
): boolean {
  return kept.includes(zonePair(profileZone, deviceZone));
}

/**
 * The stored answers with this pairing added, newest last and capped.
 *
 * Re-answering moves a pairing to the end rather than duplicating it, so the
 * zone somebody keeps returning to is the last one to fall off.
 */
export function rememberZone(
  kept: readonly string[],
  profileZone: string,
  deviceZone: string,
): string[] {
  const key = zonePair(profileZone, deviceZone);
  return [...kept.filter((v) => v !== key), key].slice(-KEPT_ZONES_LIMIT);
}
